import React, { useState, useEffect } from 'react';
import './Admin.css';
import { generateTicketPDF, generateTicketPNG } from '../utils/ticketExport';
import TicketDisplay from './TicketDisplay';
import AdminLogin from './AdminLogin';

const PREDEFINED_REQUESTS = [
  'Chci, aby pro stát pracovali odborníci, ne kamarádi a známí politiků',
  'Chci, aby veřejné zakázky vyhrávala nejvyšší kvalita nakoupené služby, ne sponzoři vítězů voleb',
  'Chci dostat důchod včas, ne až za půl roku',
  'Chci, aby vzdělávání našich dětí řídili experti, ne loutky politických stran',
  'Chci, aby Česká republika prosperovala, ne aby dělala kroky zpět'
];

function Admin() {
  const [stats, setStats] = useState(null);
  const [requestStats, setRequestStats] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [customOnly, setCustomOnly] = useState(false);

  const filteredTickets = customOnly
    ? tickets.filter(t => !PREDEFINED_REQUESTS.includes(t.request))
    : tickets;

  // Kontrola autentizace při načtení komponenty
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/admin/check-auth', {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
    } catch (err) {
      console.error('Chyba při kontrole autentizace:', err);
      setIsAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Funkce pro načtení dat
  const fetchData = () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchWithAuth = (url, errorLabel) =>
      fetch(url, { credentials: 'include' }).then(r => {
        if (r.status === 401) {
          const err = new Error('Unauthorized');
          err.status = 401;
          throw err;
        }
        if (!r.ok) throw new Error(errorLabel + ': ' + r.status);
        return r.json();
      });

    Promise.all([
      fetchWithAuth('/api/admin/stats', 'Chyba při načítání statistik'),
      fetchWithAuth('/api/admin/stats/requests', 'Chyba při načítání statistik požadavků'),
      fetchWithAuth('/api/admin/tickets/all', 'Chyba při načítání lístků')
    ])
      .then(([statsData, requestStatsData, ticketsData]) => {
        setStats(statsData);
        setRequestStats(requestStatsData);
        setTickets(ticketsData);
        setLoading(false);
      })
      .catch(err => {
        if (err.status === 401) {
          setIsAuthenticated(false);
          setStats(null);
          setRequestStats([]);
          setTickets([]);
          setError(null);
          setLoading(false);
          return;
        }
        console.error('Chyba při načítání dat:', err);
        setError(err.message);
        setLoading(false);
      });
  };

  // Načíst data pouze pokud je uživatel autentizován
  useEffect(() => {
    fetchData();
  }, [isAuthenticated]);

  // Automatický refresh každých 30 sekund
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchData();
    }, 30000); // 30 sekund

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setIsAuthenticated(false);
      setStats(null);
      setRequestStats([]);
      setTickets([]);
    } catch (err) {
      console.error('Chyba při odhlašování:', err);
    }
  };

  const handleGeneratePDF = async (ticket) => {
    setGeneratingId(ticket.id);
    try {
      await generateTicketPDF(ticket);
    } catch (err) {
      alert('Chyba při generování PDF: ' + err.message);
    } finally {
      setGeneratingId(null);
    }
  };

  const handleGeneratePNG = async (ticket, aspectRatio) => {
    setGeneratingId(`${ticket.id}-${aspectRatio}`);
    try {
      await generateTicketPNG(ticket, aspectRatio, false); // Admin download - netrackovat
    } catch (err) {
      alert('Chyba při generování PNG: ' + err.message);
    } finally {
      setGeneratingId(null);
    }
  };

  const shareOnFacebook = (ticket) => {
    const ticketUrl = `${window.location.origin}/ticket/${ticket.id}`;
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ticketUrl)}`;
    window.open(facebookShareUrl, '_blank');
  };

  // Zobrazit loading při kontrole autentizace
  if (checkingAuth) {
    return (
      <div className="admin-container">
        <div className="admin-box">
          <h1>Admin</h1>
          <p>Načítám...</p>
        </div>
      </div>
    );
  }

  // Zobrazit přihlašovací formulář pokud není autentizován
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-box">
          <h1>Admin</h1>
          <p>Načítám...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-box">
          <h1>Admin</h1>
          <p className="error">Chyba: {error}</p>
        </div>
      </div>
    );
  }


  return (
    <div className="admin-container">
      <div className="admin-layout">
        <div className="admin-left-column">
          <div className="admin-box">
            <div className="admin-header">
              <h1>Statistiky systému</h1>
              <button onClick={handleLogout} className="logout-button">
                Odhlásit se
              </button>
            </div>
            {stats && (
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-label">Celkem lístků:</div>
                  <div className="stat-value">{stats.totalTickets}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Celkem uživateli staženo PNG:</div>
                  <div className="stat-value">{stats.totalDownloads || 0}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Celkem kliknutí na FB sdílení:</div>
                  <div className="stat-value">{stats.totalFbShares || 0}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Celková doba čekání:</div>
                  <div className="stat-value">
                    {tickets.reduce((total, ticket) => {
                      const years = ticket.expected_year - new Date(ticket.created_at.includes('T') ? ticket.created_at : ticket.created_at.replace(/\s+/, 'T') + 'Z').getFullYear();
                      return total + years;
                    }, 0)} let
                  </div>
                </div>
                {stats.oldestTicket && (
                  <div className="stat-item">
                    <div className="stat-label">První lístek:</div>
                    <div className="stat-value">
                      {new Date(stats.oldestTicket.includes('T') ? stats.oldestTicket : stats.oldestTicket.replace(/\s+/, 'T') + 'Z').toLocaleString('cs-CZ', { timeZone: 'Europe/Prague' })}
                    </div>
                  </div>
                )}
                {stats.newestTicket && (
                  <div className="stat-item">
                    <div className="stat-label">Poslední lístek:</div>
                    <div className="stat-value">
                      {new Date(stats.newestTicket.includes('T') ? stats.newestTicket : stats.newestTicket.replace(/\s+/, 'T') + 'Z').toLocaleString('cs-CZ', { timeZone: 'Europe/Prague' })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="admin-box">
            <h2>Potřeby od státu</h2>
            <div className="request-stats-grid">
              {requestStats.map((item, index) => (
                <div key={index} className="request-stat-item">
                  <div className="request-stat-label">
                    {item.request}
                  </div>
                  <div className="request-stat-value">{item.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="admin-box tickets-box">
          <div className="tickets-header">
            <h2>
              {customOnly ? 'Občanské lístky' : 'Všechny lístky'} (od nejnovějšího)
              <span className="tickets-count"> · {filteredTickets.length}</span>
            </h2>
            <div className="filter-segmented" role="tablist" aria-label="Filtr lístků">
              <button
                type="button"
                role="tab"
                aria-selected={!customOnly}
                className={`filter-segmented-option${!customOnly ? ' is-active' : ''}`}
                onClick={() => setCustomOnly(false)}
              >
                Všechny
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={customOnly}
                className={`filter-segmented-option${customOnly ? ' is-active' : ''}`}
                onClick={() => setCustomOnly(true)}
              >
                Občanské
              </button>
            </div>
          </div>
          <div className="tickets-list">
            {filteredTickets.length === 0 ? (
              <p className="no-tickets">Žádné lístky</p>
            ) : (
              filteredTickets.map(ticket => (
                <div key={ticket.id} className="ticket-item">
                  <div
                    className="ticket-item-info"
                    onClick={() => setSelectedTicket(ticket)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="ticket-item-number">#{ticket.ticket_number}</div>
                    <div className="ticket-item-request">{ticket.request}</div>
                    <div className="ticket-item-date">
                      {new Date(ticket.created_at.includes('T') ? ticket.created_at : ticket.created_at.replace(/\s+/, 'T') + 'Z').toLocaleDateString('cs-CZ', { timeZone: 'Europe/Prague' })}
                    </div>
                    <div className="ticket-item-downloads">
                      📥 Uživateli staženo PNG: {ticket.download_count || 0}
                    </div>
                    <div className="ticket-item-downloads">
                      📤 Kliknutí na FB sdílení: {ticket.fb_share_count || 0}
                    </div>
                  </div>
                  <div className="ticket-item-actions">
                    <button
                      className="action-button action-button-pdf"
                      onClick={() => handleGeneratePDF(ticket)}
                      disabled={generatingId === ticket.id}
                    >
                      {generatingId === ticket.id ? '...' : 'PDF'}
                    </button>
                    <button
                      className="action-button action-button-facebook"
                      onClick={() => shareOnFacebook(ticket)}
                    >
                      Facebook
                    </button>
                    <button
                      className="action-button action-button-png"
                      onClick={() => handleGeneratePNG(ticket, '4:5')}
                      disabled={generatingId === `${ticket.id}-4:5`}
                    >
                      {generatingId === `${ticket.id}-4:5` ? '...' : 'PNG 4:5'}
                    </button>
                    <button
                      className="action-button action-button-png"
                      onClick={() => handleGeneratePNG(ticket, '1:1')}
                      disabled={generatingId === `${ticket.id}-1:1`}
                    >
                      {generatingId === `${ticket.id}-1:1` ? '...' : 'PNG 1:1'}
                    </button>
                    <button
                      className="action-button action-button-png"
                      onClick={() => handleGeneratePNG(ticket, '9:16')}
                      disabled={generatingId === `${ticket.id}-9:16`}
                    >
                      {generatingId === `${ticket.id}-9:16` ? '...' : 'PNG 9:16'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <TicketDisplay
              ticket={selectedTicket}
              onBack={() => setSelectedTicket(null)}
              isNewTicket={false}
              hideBackButton={true}
              showCloseButton={true}
              onClose={() => setSelectedTicket(null)}
              isAdminView={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
